import { Button } from "$components/button/button";
import { PlusBigIcon } from "$components/icons";
import { makeStyles } from "$components/mui-styles";
import {
  useConvertFromPenceFactory,
  useConvertFromPoundsFactory,
} from "$routes/_lib/use-convert-currency";
import { Grid, TextField } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import React, { useCallback } from "react";
import { get, useFieldArray, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { AMOUNT, AMOUNT_IN_PENCE, TRANSACTIONS } from "./formConstants";

const useStyles = makeStyles((theme) => ({
  containerGrid: {
    flex: 1,
  },
  transactionRow: {
    position: "relative",
    marginBottom: "10px",
  },
  currencyGrid: {
    paddingRight: "15px",
  },
  icon: {
    width: "14px",
  },
  newTransactionButton: {
    textAlign: "left",
    paddingLeft: 0,
  },
  deleteBtn: {
    cursor: "pointer",
    color: theme.palette.primary.main,
    position: "absolute",
    right: "5px",
    top: "20px",
  },
  inputRoot: {
    fontWeight: 600,
  },
  disabled: {
    color: theme.palette.text.primary,
  },
  label: {
    color: theme.palette.grey[600],
    fontSize: "14px",
    [theme.breakpoints.down("xs")]: {
      fontSize: "10px",
    },
  },
}));

export function useBaseTransaction() {
  const penceToPounds = useConvertFromPenceFactory();

  return useCallback(
    ({ amountInPence = 0, currency = "" } = {}) => ({
      amountInPence,
      amount: penceToPounds(amountInPence),
      currency,
      method: "CASH",
    }),
    [penceToPounds],
  );
}

type FormValues = {
  transactions: { type?: string; amountInPence: number; amount: number }[];
};

export const Transactions = ({ currency }) => {
  const classes = useStyles();
  const { setValue, formState, control, register } =
    useFormContext<FormValues>();
  const { formatMessage } = useIntl();
  const baseTransaction = useBaseTransaction();
  const poundsToPence = useConvertFromPoundsFactory();
  const { fields: transactions, ...arrayHelpers } = useFieldArray<FormValues>({
    name: TRANSACTIONS,
    control,
  });
  const transactionAmountLabel = formatMessage({
    id: "invoicing.transaction.amount.label",
    defaultMessage: "Transaction amount",
  });
  const addTransactionLabel = formatMessage({
    id: "invoicing.transaction.add.label",
    defaultMessage: "Add Transaction",
  });

  return (
    <Grid item className={classes.containerGrid}>
      {transactions.map((transaction, i) => {
        if (transaction.type === "WAIVER") {
          return null;
        }

        const TRANSACTION = `${TRANSACTIONS}.${i}`;
        const TRANSACTION_AMOUNT_NAME = `${TRANSACTIONS}.${i}.${AMOUNT}`;
        const TRANSACTION_AMOUNT_IN_PENCE_NAME = `${TRANSACTIONS}.${i}.${AMOUNT_IN_PENCE}`;
        const amountInPenceError = get(
          formState.errors,
          TRANSACTION_AMOUNT_IN_PENCE_NAME,
        );

        return (
          <Grid
            container
            key={TRANSACTION}
            data-testid={TRANSACTION}
            className={classes.transactionRow}
          >
            <Grid item xs={10} sm={5} md={3} className={classes.currencyGrid}>
              <TextField
                key={transaction.id}
                {...register(`${TRANSACTIONS}.${i}.${AMOUNT}`)}
                type="number"
                name={TRANSACTION_AMOUNT_NAME}
                data-testid={TRANSACTION_AMOUNT_NAME}
                label={transactionAmountLabel}
                inputMode="numeric"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setValue(
                    `${TRANSACTIONS}.${i}.${AMOUNT_IN_PENCE}`,
                    poundsToPence(e.target.value),
                    { shouldValidate: true },
                  );
                }}
                InputLabelProps={{ classes: { root: classes.label } }}
                InputProps={{
                  classes: {
                    disabled: classes.disabled,
                    root: classes.inputRoot,
                  },
                  inputProps: { min: 0, step: 1 },
                }}
                helperText={
                  amountInPenceError
                    ? formatMessage({
                        id: "invoicing.takePayment.transaction.amount.required",
                        defaultMessage: "Required",
                      })
                    : null
                }
                error={!!amountInPenceError}
              />
            </Grid>
            {transactions.filter((t) => t.type !== "WAIVER").length > 1 && (
              <DeleteIcon
                className={classes.deleteBtn}
                onClick={() => arrayHelpers.remove(i)}
                data-testid={`delete-button-${i}`}
              />
            )}
          </Grid>
        );
      })}
      <Grid item xs={4}>
        <Button
          color="secondary"
          className={classes.newTransactionButton}
          onClick={() =>
            arrayHelpers.insert(
              transactions.length,
              baseTransaction({ currency }),
            )
          }
          startIcon={<PlusBigIcon className={classes.icon} />}
        >
          {addTransactionLabel}
        </Button>
      </Grid>
    </Grid>
  );
};
